/**
 * ═══════════════════════════════════════════════════════════════════════
 * СКЛОНЕНИЕ ПРИ ЧИСЛЕ — ОДНА ЗАПИСЬ НА ОБА ДЕРЕВА.
 *
 * «1 рабочий день», «2 рабочих дня», «5 рабочих дней». Правило русского
 * языка, а не оформление: «осталось 1 рабочих дней» человек читает как
 * ошибку системы и перестаёт верить числу рядом.
 *
 * ФАЙЛ-БЛИЗНЕЦ: `src/common/sklonenie.ts` и `web/src/sklonenie.ts`
 * совпадают посимвольно, сверка — в `src/common/twins.spec.ts`.
 * Ввоза здесь нет и быть не может: близнец с зависимостью — уже
 * не близнец, `./что-нибудь` в двух деревьях ведёт в разные файлы.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЭТОТ ФАЙЛ ЗАВЕДЁН, ПОТОМУ ЧТО КОПИЙ В КАБИНЕТЕ ДВАДЦАТЬ ШЕСТЬ.
 *
 * Число я называл трижды и дважды ошибся. Искал по именам функций —
 * вышло три. Сторож `sklonenie.spec.ts` ищет по ПРИМЕТЕ правила,
 * а не по имени, — вышло семь, и я записал семь. Настоящих двадцать
 * шесть: семь я насчитал в своей песочнице, где лежит несколько
 * десятков файлов кабинета вместо нескольких сотен.
 *
 * То есть я померил свойство репозитория, стоя внутри четырёх его
 * процентов. Разбор был верным, дерево — нет.
 *
 * КОПИИ РАЗНЫЕ, И ЭТО ГЛАВНЫЙ ДОВОД ЗА ЭТОТ ФАЙЛ. Двадцать берут
 * число как есть, шесть — по модулю. Пока отрицательное никому
 * из двадцати не достаётся, ответ у всех один; достанется —
 * разойдутся, и «просрочено на 1 дней» появится в одном углу
 * продукта, где его никто не сверяет с остальными двадцатью пятью.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СКОЛЬКО ТАКИХ ЗАПИСЕЙ НА СЕРВЕРЕ — НЕ ПОСЧИТАНО, И ЭТО НАРОЧНО.
 *
 * Тот же счёт по `src/` я поставить не могу: моя песочница держит
 * малую часть службы, и любое число, названное отсюда, будет меньше
 * настоящего — ровно так я и ошибся в кабинете. Сторож, зеленеющий
 * на заниженном числе, хуже отсутствующего: он выдаёт долг за порядок.
 *
 * Одну такую запись я нашёл прямо здесь и починил — `freeze-credit.ts`
 * (см. ниже). Перепись остальных — работа на полном дереве, и она
 * записана задачей, а не числом в проверке.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧИСЛО БЕРЁТСЯ ПО МОДУЛЮ — И ЭТО НЕ МЕЛОЧЬ.
 *
 * Остаток срока бывает отрицательным: −1 значит «просрочено на один
 * рабочий день». Без `Math.abs` правило уходит в ветку «прочее»
 * и выдаёт «просрочено на 1 дней». У эталона `Math.abs` стоит;
 * у двадцати наших копий его нет — они просто не встречали
 * отрицательных.
 * ═══════════════════════════════════════════════════════════════════════
 */
export type ТриФормы = readonly [string, string, string];

export function склонение(n: number, формы: ТриФормы): string {
  const число = Math.abs(Math.trunc(n));
  const сотня = число % 100;
  if (сотня >= 11 && сотня <= 19) return формы[2];
  const единица = число % 10;
  if (единица === 1) return формы[0];
  if (единица >= 2 && единица <= 4) return формы[1];
  return формы[2];
}

/** «5 рабочих дней» — число и склонённое слово вместе. */
export function счётом(n: number, формы: ТриФормы): string {
  return `${n} ${склонение(n, формы)}`;
}

export const РАБОЧИЕ_ДНИ: ТриФормы = Object.freeze([
  'рабочий день',
  'рабочих дня',
  'рабочих дней',
]) as ТриФормы;

/**
 * «1 сутки», «2 суток», «5 суток».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПЕРВАЯ ФОРМА ЗДЕСЬ — «СУТКИ», А НЕ «СУТОК», И НА ЭТОМ УЖЕ ОШИБЛИСЬ.
 *
 * «Сутки» — слово только множественного числа, и при единице оно
 * остаётся «сутки»: одни сутки, но двое суток. Поэтому первая форма
 * отличается от второй и третьей, а вторая от третьей — нет.
 *
 * В `freeze-credit.ts` стояла своя запись этого правила, и обе её
 * ветки возвращали «суток»:
 *
 *     if (сотня >= 11 && сотня <= 14) return 'суток';
 *     return единица === 1 ? 'суток' : 'суток';
 *
 * То есть остаток от деления считался дважды и выбрасывался. Журнал
 * писал «Возврат 1 суток за время заморозки» — а однодневная заморозка
 * там самая частая: соседняя проверка в том же файле так и называется,
 * «два часа заморозки — одни сутки».
 *
 * Проверки на `freezeReason` были — на четырёх сутках и на двух.
 * Единицу, единственное число, где запись ошибалась, не спросил никто.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const СУТКИ: ТриФормы = Object.freeze(['сутки', 'суток', 'суток']) as ТриФормы;

/** «11 календарных дней» — для сроков, которые Оферта считает календарными. */
export const КАЛЕНДАРНЫЕ_ДНИ: ТриФормы = Object.freeze([
  'календарный день',
  'календарных дня',
  'календарных дней',
]) as ТриФормы;
